import clsx from "clsx";
import { type FC, ReactNode } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { CourseUserOptionsMenu } from "../home/admin/users/AdminUserOptionsMenu";

import { CourseUserType, UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { ListItem } from "mds/components/ListItem";
import { showConfirmationModal } from "mds/components/Modal";
import { Tooltip } from "mds/components/Tooltip";
import {
  CircleDisabledIcon,
  HandRaiseCircleIcon,
  PersonJoinedIcon,
  PersonSingleIcon,
  PresentPlayBackgroundIcon,
  PresentPlayIcon,
  ReactionVoteNoIcon,
  ReactionVoteYesIcon,
  ScreenshareIcon,
} from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  CourseUserActivityStatus,
  selectActivityStatusForUser,
  selectClassUserByCourseUserId,
  selectCourseUserByUserId,
  selectCurrentCourseCodeTitle,
  selectCurrentCourseTitle,
  selectFollowingStatusForUser,
  selectUserById,
} from "store/selectors";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { getCourseResourceRole } from "utils/course";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

type EnhancedCourseUserActivityStatus =
  | CourseUserActivityStatus
  | "disabled"
  | "following"
  | "presenting"
  | "screensharing";

// NOTE: this is duplicated from helpers.tsx because we want XS icons here but medium icons elsewhere
const userActivityIcons: Record<EnhancedCourseUserActivityStatus, ReactNode> = {
  active: <PersonJoinedIcon size="xs" statusColor="green" />,
  inactive: <PersonJoinedIcon size="xs" statusColor="green" />,
  offline: <PersonSingleIcon size="xs" />,
  disabled: <CircleDisabledIcon size="xs" />,
  following: <PresentPlayIcon size="xs" />,
  presenting: (
    <span className="text-green">
      <PresentPlayBackgroundIcon size="xs" />
    </span>
  ),
  screensharing: (
    <span className="text-green">
      <ScreenshareIcon size="xs" />
    </span>
  ),
};

const statusTooltipText: Record<EnhancedCourseUserActivityStatus, string> = {
  active: "",
  inactive: "",
  offline: "",
  disabled: "",
  following: t("presentation.following"),
  presenting: t("presentation.presenting"),
  screensharing: t("av.screenshare.banner.screensharing"),
};

const handleDisableUser = (
  user: UserType,
  courseUser: CourseUserType,
  courseTitle: string,
  courseCodeTitle: string,
) => {
  const resourceRole = getCourseResourceRole(courseUser.role);
  const role = t(`roles.course_${resourceRole}`);
  const name = getUserFullName(user);
  const courseName = t("common.course_code_and_title", {
    course_title: courseTitle,
    course_code_title: courseCodeTitle,
  });

  const modalAction = courseUser.is_disabled ? "grant" : "restrict";

  return showConfirmationModal({
    title: t(`user_options_menu.role.${modalAction}_title`, { role }),
    labelText: t(`user_options_menu.role.${modalAction}_label`, {
      name,
      course: courseName,
    }),
    confirmButtonText: t(`user_options_menu.role.${modalAction}_button_text`),
    confirmButtonKind: courseUser.is_disabled ? "primary" : "destructive",
    onConfirm: () => {
      storeApi.course_users.partial_update({
        id: courseUser.id,
        is_disabled: !courseUser.is_disabled,
      });
    },
  });
};

interface CourseUserItemProps {
  userId: string;
  canImpersonate: boolean;
  canModifyUsers: boolean;
  presentingUserId?: string | null;
  screensharingUserId?: string | null;
}

export const CourseUserItem: FC<CourseUserItemProps> = ({
  userId,
  canImpersonate,
  canModifyUsers,
  presentingUserId,
  screensharingUserId,
}) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const user = useAppSelector((state) => selectUserById(state, userId));
  const courseActivityStatus = useAppSelector((state) =>
    selectActivityStatusForUser(state, userId),
  );
  const isFollowing = useAppSelector((state) => selectFollowingStatusForUser(state, userId));
  const courseUser = useAppSelector((state) => selectCourseUserByUserId(state, userId));
  const classUser = useAppSelector((state) =>
    selectClassUserByCourseUserId(state, courseUser?.id || ""),
  );
  const currentCourseTitle = useAppSelector(selectCurrentCourseTitle);
  const currentCourseCodeTitle = useAppSelector(selectCurrentCourseCodeTitle);

  if (!user || !courseUser) return null;

  const isPresenting = userId === presentingUserId;
  const isScreensharing = userId === screensharingUserId;
  const status = courseUser.is_disabled
    ? "disabled"
    : isScreensharing
      ? "screensharing"
      : isPresenting
        ? "presenting"
        : isFollowing
          ? "following"
          : courseActivityStatus;

  return (
    <ListItem
      className={clsx("flex items-center justify-between gap-2 text-sm", {
        offline:
          courseActivityStatus === CourseUserActivityStatus.OFFLINE && !courseUser.is_disabled,
      })}
      key={userId}
    >
      <div className="flex flex-row items-center gap-2" title={user.email}>
        <Tooltip content={statusTooltipText[status]}>{userActivityIcons[status]}</Tooltip>
        <span className={clsx({ "text-black-tint-55": courseUser.is_disabled })}>
          {getUserFullName(user)}
        </span>
      </div>
      <div className="flex flex-row items-center gap-1">
        {classUser?.hand_raised_at && <HandRaiseCircleIcon aria-label={t("av.raised_hand")} />}
        {classUser?.vote === "yes" && <ReactionVoteYesIcon aria-label={t("reactions.vote_yes")} />}
        {classUser?.vote === "no" && <ReactionVoteNoIcon aria-label={t("reactions.vote_no")} />}
        {canModifyUsers && (
          <CourseUserOptionsMenu
            courseUser={courseUser}
            user={user}
            onChangeRole={(newRole) => {
              storeApi.course_users
                .partial_update({
                  id: courseUser.id,
                  role: newRole,
                })
                .catch(
                  toastLocalizedOperationCatcher("update_failure", {
                    item: t("glossary.user_role"),
                  }),
                );
            }}
            onClickDisable={() =>
              handleDisableUser(user, courseUser, currentCourseTitle, currentCourseCodeTitle)
            }
            onImpersonate={
              canImpersonate
                ? () => {
                    createImpersonationUrl(user.id).then(copyToClipboard);
                  }
                : undefined
            }
          />
        )}
      </div>
    </ListItem>
  );
};
